/**
 * Single source of truth for the free-vs-paid comparison shown beside the
 * store offers (web UpgradeModal). Numeric caps come from the backend
 * (`GET /api/billing/plans` and `GET /api/auth/me`); what a purchase grants
 * comes from the offer catalog (`./offers`, contracts/monetization/offers.json).
 * This module only defines the rows and how to format them — change a row
 * here once, every surface updates.
 */
import type { PlanLimits } from './planLimits';
import { MAX_ROOM_CAP, STORE_OFFERS } from './offers';

/** Default free compute-credit allowance; matches backend `EVAL_POOL_FREE_SIZE`.
 *  One credit = one physics simulation (a Compute, a placement solve, or a
 *  panel optimization). Only ever a placeholder for the frame before
 *  `GET /api/billing/plans` answers — the server is the source of truth. */
export const EVAL_POOL_FREE_SIZE = 5;

/** Resolved numeric caps the UI needs to render feature rows. */
export interface PlanCatalogLimits {
  evalPoolTotal: number;
  maxRooms: number;
  maxDesignsPerRoom: number;
}

export function planCatalogLimitsFromApi(input: {
  eval_pool_total?: number;
  limits?: PlanLimits | null;
}): PlanCatalogLimits {
  return {
    evalPoolTotal: input.eval_pool_total ?? EVAL_POOL_FREE_SIZE,
    maxRooms: input.limits?.max_rooms ?? 1,
    maxDesignsPerRoom: input.limits?.max_designs_per_room ?? 1,
  };
}

/** Every row's id: a surface keying something off a row (the plans screen's
 *  icons) keys a `Record<PlanFeatureId, …>`, so a new row it forgets is a
 *  compile error rather than a blank. */
export type PlanFeatureId =
  | 'treatment_design'
  | 'before_after'
  | 'simulations'
  | 'rooms'
  | 'placement_optimizer'
  | 'room_analysis'
  | 'system_design';

export interface PlanFeatureRow {
  id: PlanFeatureId;
  label: string;
  /** What the feature does for the room, in words: the outcome a buyer is
   *  paying for. The label names the feature; this says why it matters. */
  sub?: string;
  /** Without a purchase. */
  free: boolean | string;
  /** With any offer that grants it (a room bundle or Unlimited). */
  pro: boolean | string;
}

function roomCapLabel(n: number): string {
  return n === 1 ? '1 room' : `${n} rooms`;
}

/** The fewest compute credits any paid offer adds, from the catalog: a
 *  bundle's credits, or one pack of the credits offer. */
function smallestCreditGrant(): number | null {
  const grants = STORE_OFFERS
    .map((o) => o.grants.credits ?? o.grants.credits_per_unit)
    .filter((n): n is number => typeof n === 'number' && n > 0);
  return grants.length > 0 ? Math.min(...grants) : null;
}

/** Build the free-vs-paid comparison rows from live server limits and the
 *  offer catalog.
 *
 *  Order is the argument. A purchase sells one idea: the free tier shows you
 *  the problem, a purchase fixes it. So what a purchase adds comes first, most
 *  valuable first, each with its outcome; what every account has comes last.
 *  Only what the offers really grant is listed: panel placement (and the
 *  treated room it lets you compare against), more simulations, more rooms.
 *  A "—" means the feature is absent without a purchase, never merely capped.
 *
 *  Rewritten 2026-09-27 when the store offers (origin) replaced the premium
 *  rooms (ADR 013): "Room audio simulation" had sat in the paid rows although
 *  simulation is metered, not withheld, and "Product picks & DIY parts list"
 *  sold a detail of treatment as a feature of its own. */
export function buildPlanFeatureRows(limits: PlanCatalogLimits): PlanFeatureRow[] {
  const { evalPoolTotal, maxRooms } = limits;
  const minCredits = smallestCreditGrant();
  return [
    {
      id: 'treatment_design',
      label: 'Acoustic treatment design',
      sub: 'Panels and bass traps placed where they fix your room, with what to buy or build',
      free: false,
      pro: true,
    },
    {
      id: 'before_after',
      label: 'Before & after treatment',
      sub: 'See the difference before you buy a single panel',
      free: false,
      pro: true,
    },
    {
      id: 'simulations',
      label: 'Simulations',
      sub: 'Every Compute, placement run and panel optimization',
      free: String(evalPoolTotal),
      pro: minCredits === null ? 'Unlimited' : `+${minCredits} to unlimited`,
    },
    {
      id: 'rooms',
      label: 'Rooms',
      sub: 'Keep every room you design',
      free: roomCapLabel(maxRooms),
      pro: `Up to ${MAX_ROOM_CAP}`,
    },
    { id: 'placement_optimizer', label: 'Speaker & seat optimizer', free: true, pro: true },
    { id: 'room_analysis', label: 'Room model & bass analysis', free: true, pro: true },
    { id: 'system_design', label: 'Gear, compatibility checks & furniture', free: true, pro: true },
  ];
}

/** Post-simulation nudge copy. `remaining` is null for unlimited subscribers.
 *
 *  `remaining` can exceed `total`: a handful of accounts still hold credits
 *  from the retired one-time packs, and the backend folds those into the same
 *  balance (they buy a simulation now, not a room). "7 of 5 free simulations
 *  left" is nonsense, so the denominator is dropped whenever the balance runs
 *  past the free allowance. */
export function evalPoolRemainingMessage(
  remaining: number | null,
  total: number,
): string | null {
  if (remaining === null) return null;
  if (remaining <= 0) return 'You have used all your free simulations.';
  if (remaining === 1) return '1 free simulation left.';
  if (remaining > total) return `${remaining} simulations left.`;
  return `${remaining} of ${total} free simulations left.`;
}

/** How urgent the remaining balance is, for surfaces that colour it.
 *
 *  Split out of the chip that renders it so the thresholds are one testable
 *  rule rather than inline ternaries per surface — web's editor chip today,
 *  mobile's next. The band is proportional rather than a fixed "yellow at 2",
 *  so it still means something if EVAL_POOL_FREE_SIZE moves: at the shipped
 *  allowance of 3 that lands as 3 ample, 2-1 low, 0 spent. */
export type ComputeCreditTone = 'ample' | 'low' | 'spent';

export function computeCreditTone(remaining: number, total: number): ComputeCreditTone {
  if (remaining <= 0) return 'spent';
  // A balance past the free allowance (leftover purchased credits) is never
  // urgent, and Math.ceil on a bad `total` must not make it look urgent.
  if (remaining > total) return 'ample';
  const lowAtOrBelow = Math.max(1, Math.ceil(total * 0.4));
  return remaining <= lowAtOrBelow ? 'low' : 'ample';
}

/** The chip label. Deliberately just the number and the unit — the chip sits in
 *  a header next to four other controls, and "3 credits left on the free plan"
 *  is a sentence where a count will do. */
export function computeCreditLabel(remaining: number): string {
  return `${Math.max(0, remaining)} ${remaining === 1 ? 'credit' : 'credits'}`;
}

/** What the unit above actually MEANS, for the one surface with room to say it:
 *  the chip's hover explanation.
 *
 *  "Credit" is the only word in this product the user cannot decode from the
 *  screen it appears on — the count answers "how many", never "of what", and
 *  the answer is not guessable (it is not per room, per panel, or per day). It
 *  lives here rather than in the chip because mobile shows the same balance and
 *  must not invent a second definition of the same noun. */
export const COMPUTE_CREDIT_EXPLAINER =
  'A credit is spent whenever a physics simulation is computed.';

/** Whether to show a soft upgrade nudge (value shown, not hard-blocked yet). */
export function shouldNudgeUpgradeAfterSimulation(remaining: number | null): boolean {
  return remaining !== null && remaining >= 0 && remaining <= 1;
}

export function isEvalPoolExhaustedDetail(detail: string | undefined): boolean {
  return detail === 'eval_pool_exhausted';
}

export function isSubscriptionRequiredDetail(detail: string | undefined): boolean {
  return detail === 'subscription_required';
}

export function isRoomLimitDetail(detail: string | undefined): boolean {
  return detail === 'room_limit';
}

/** Parse FastAPI error body text for the `detail` string. */
export function parseApiDetail(body: string): string | undefined {
  try {
    const parsed = JSON.parse(body) as { detail?: unknown };
    return typeof parsed.detail === 'string' ? parsed.detail : undefined;
  } catch {
    return undefined;
  }
}
